import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { METHODS, SERVICES } from '../../api/constants';
import { commonMoodlePayload } from '../../config';
import { makeRequest } from '../../api/api';
import { MOODLE_BACKEND_ROUTES } from '../../config/apiRoutes';
import { fetchSection, markCourseActivity } from './programTimeline';
import { fetchResumeTime } from './paidActivitiesSlice';
import { toast } from 'react-toastify';
import SuccessToast from '../../@common/SuccessToast/SuccessToast';
import { customToast } from '../../constant/constant';

export const getSectionDetails = createAsyncThunk(
  'pageActivity/getSectionDetails',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          cmid: param?.cmid,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_SECTION_DETAILS,
          wstoken: thunkApi.getState().auth.token
        }
      );
      if (
        response.status == 200 &&
        !(response?.data?.errorcode || response?.data?.exception)
      ) {
        thunkApi.dispatch(
          fetchSection({
            wstoken: thunkApi.getState().auth.token,
            courseid: response?.data?.courseid,
            sectionid: response?.data?.section
          })
        );
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const fetchActivityDetails = createAsyncThunk(
  'pageActivity/fetchActivityDetails',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param?.data,
          ...commonMoodlePayload,
          wstoken: thunkApi.getState().auth.token
        }
      );
      thunkApi.dispatch(
        markCourseActivity({
          activitytype: param?.extra?.type,
          moduleid: param?.data?.cmid || param?.extra?.cmid
        })
      );

      if (param?.extra?.type == 'videofile') {

        thunkApi.dispatch(
          fetchResumeTime({
            'args[1][name]': 'cmid',
            'args[1][value]': param?.extra?.cmid,
            'args[0][name]': 'courseid',
            'args[0][value]': param?.data?.courseid
          })
        );
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const fetchFeedbackQuestionData = createAsyncThunk(
  'pageActivity/fetchFeedbackQuestionData',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wstoken: thunkApi.getState().auth.token
        }
      );
      return { data: response.data };
    } catch (e) {
      thunkApi.rejectWithValue(e);
      toast.error('Something went wrong!!');
    }
  }
);

export const postFeedbacksSubmission = createAsyncThunk(
  'appFeedbackWidget/postFeedbacks',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.POST,
        {
          ...param,
          ...commonMoodlePayload,
          wstoken: thunkApi.getState().auth.token
        }
      );
      if (
        response.status == 200 &&
        (response?.data?.errorcode || response?.data?.exception || !response?.data?.completed)
      ) {
        customToast(
          'Something went wrong!!',
          '',
          'error',
          5000,
        )
        return thunkApi.rejectWithValue(response.data);
      }
      return { data: response.data };
    } catch (e) {
      thunkApi.rejectWithValue(e);
      toast.error('Something went wrong!!');
    }
  }
);

export const fetchSessionRecording = createAsyncThunk(
  'pageActivity/fetchSessionRecording',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wstoken: thunkApi.getState().auth.token,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_SESSION_RECORDING,
          'args[1][name]': 'courseid',
          'args[1][value]': thunkApi.getState().pageActivity.section.courseid
        }
      );
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const fetchDuplicateSessionRecording = createAsyncThunk(
  'pageActivity/fetchDuplicateSessionRecording',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wstoken: thunkApi.getState().auth.token,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_DUPLICATE_SESSION_RECORDING,
        }
      );
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const pageActivity = createSlice({
  name: 'pageActivity',
  initialState: {
    tempActivityData: {},
    loading: true,
    section: {},
    activityLoading: true,
    activityData: {},
    zoomRecording: {},
    recordingLoader: false,
    questionsData: null,
    submittedState: false,
    submittedLoading: false,
    submittedLoadingQuestion: false
  },
  reducers: {
    setActivity: (state, action) => {
      state.section = {
        ...state.section,
        id: action.payload
      };
    },
    setSection: (state, action) => {
      state.section = {
        ...state.section,
        section: action.payload
      };
    },
    setActivityData: (state, action) => {
      Object.keys(action.payload).map((val) => {
        state[val] = action.payload[val];
      });
    },
    updateResponse: (state, action) => {
      const { questionId, response } = action.payload;
      state.responses[questionId] = response;
    },
    clearQuestionsData: (state) => {
      state.questionsData = null;
    },
    resetPageActivity: (state) => {
      state.loading = true;
      state.section = {};
      state.activityLoading = true;
      state.activityData = {};
      state.zoomRecording = {};
      state.recordingLoader = false;
      state.questionsData = null;
      state.submittedState = false;
      state.submittedLoading = false;
      state.submittedLoadingQuestion = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchActivityDetails.pending, (state) => {
      state.activityLoading = true;
      state.activityData = {};
    });
    builder.addCase(fetchActivityDetails.fulfilled, (state, action) => {
      console.log(action.meta.arg.extra.type);

      state.activityLoading = false;
      if (
        action?.meta?.arg?.extra?.type === 'feedback' &&
        action?.payload?.capabilites?.isalreadysubmitted
      ) {
        state.submittedState = true;
      }
      state.activityData = action?.payload || {};
      state.tempActivityData = action?.payload || {};
    });
    builder.addCase(fetchActivityDetails.rejected, (state) => {
      state.activityLoading = false;
      state.activityData = {};
    });
    builder.addCase(getSectionDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSectionDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.section = action?.payload || {};
    });
    builder.addCase(getSectionDetails.rejected, (state) => {
      state.loading = false;
      state.section = {};
    });
    builder.addCase(fetchSessionRecording.pending, (state) => {
      state.recordingLoader = true;
    });
    builder.addCase(fetchSessionRecording.fulfilled, (state, action) => {
      state.recordingLoader = false;
      state.zoomRecording = action?.payload || {};
    });
    builder.addCase(fetchSessionRecording.rejected, (state) => {
      state.recordingLoader = false;
      state.zoomRecording = {};
    });
    ////////////////////////////////////////////////////////////////////
    builder.addCase(fetchDuplicateSessionRecording.pending, (state) => {
      state.recordingLoader = true;
    });
    builder.addCase(fetchDuplicateSessionRecording.fulfilled, (state, action) => {
      state.recordingLoader = false;
      state.zoomRecording = action?.payload || {};
    });
    builder.addCase(fetchDuplicateSessionRecording.rejected, (state) => {
      state.recordingLoader = false;
      state.zoomRecording = {};
    });
    ////////////////////////////////////////////////////////////////////
    builder.addCase(fetchFeedbackQuestionData.pending, (state) => {
      state.submittedLoading = true;
    });
    builder.addCase(fetchFeedbackQuestionData.fulfilled, (state, action) => {
      state.questionsData = action.payload?.data?.items;
      state.submittedLoading = false;
    });
    builder.addCase(fetchFeedbackQuestionData.rejected, (state) => {
      state.questions = {};
      state.submittedLoading = false;
    });
    builder.addCase(postFeedbacksSubmission.pending, (state) => {
      state.submittedLoadingQuestion = true;
    });
    builder.addCase(postFeedbacksSubmission.fulfilled, (state, action) => {
      state.success = true;
      state.submittedState = true;
      state.submittedLoadingQuestion = true;
    });
    builder.addCase(postFeedbacksSubmission.rejected, (state) => {
      state.submittedState = false;
      state.submittedLoadingQuestion = true;
    });
  }
});

export const {
  setActivityData,
  setSection,
  setActivity,
  clearQuestionsData,
  resetSubmittedState,
  resetPageActivity
} = pageActivity.actions;

export default pageActivity.reducer;
